<script lang="ts" module>
  import { page } from '$app/state';

  import type { Price, ProductVariant } from '@agapi/mlc-core';
  import type { SystemStyleObject } from '@agapi/ui/panda/types';

  import type { CartStore } from '../../../stores/cart';
  import { rewriteUrl } from '../../../utils';

  export interface ProductCardProps {
    product?: ProductVariant;
    buttonLabel: string;

    loading?: boolean;
    /* Onclick for any of the onclick events */
    onclick?: (ev?: MouseEvent) => void;
    /* Callback for when the product is added to the cart */
    onProductAdded?: (ev?: MouseEvent) => void;
    /* If true, the cart drawer will not be opened */
    ignoreDrawer?: boolean;
    css?: SystemStyleObject;
  }
</script>

<script lang="ts">
  import { getContext } from 'svelte';
  import { ShoppingCartSimple, Check, X } from 'phosphor-svelte';

  import { css } from '@agapi/ui/panda/css';
  import { float, hstack } from '@agapi/ui/panda/patterns';
  import { Badge } from '@agapi/ui/badge';
  import { Button } from '@agapi/ui/button';
  import { Card, CardHeader, CardFooter } from '@agapi/ui/card';
  import { Loader } from '@agapi/ui/loader';
  import { Skeleton } from '@agapi/ui/skeleton';
  import { Image } from '@agapi/ui/image';

  import { productCardStyles } from '../product-card.styles';
  import { getLocaleFromUrl, getLocaleFormattedPrice } from '../../../utils';
  import { calculatePercentDiscount } from '../../../utils/pricing.server';
  import { variantToCartItem } from '../../../utils/transformer.client';
  import { EVENTS } from '../../../services';
  import * as m from '$lib/parachute/messages';

  const { add } = getContext<CartStore>('cart-store');

  const {
    product,
    css: cssProp,
    buttonLabel,
    onclick,
    onProductAdded,
    ignoreDrawer,
    loading,
    ...props
  }: ProductCardProps = $props();

  const classes = productCardStyles.raw({ size: 'small' });
  const image = $derived(product?.images?.[0]);

  let added = $state(false);
  let handling = $state(false);
  let showPopover = $state(false);
  let outOfStock = $state(!product?.stock);

  let buttonRef: HTMLSpanElement | null = $state(null);

  const triggerButtonShake = () => {
    buttonRef?.classList.add('shake');

    setTimeout(() => {
      buttonRef?.classList.remove('shake');
    }, 1000);
  }

  const handleCartAdd = async (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    console.log('handleCartAdd', product);

    handling = true;
    try {
      console.log('trying');

      await add(variantToCartItem(product as ProductVariant));
      added = true;

      onProductAdded?.();

      setTimeout(() => {
        added = false;
      }, 1800);

      try {
        EVENTS.emit('cart_item_add', { variant: product, quantity: 1, ignoreDrawer: ignoreDrawer ?? false });
      } catch (e) {
        // Ignore error?
      }
    } catch (error) {
      console.error(error);
      outOfStock = true;
      showPopover = true;
      triggerButtonShake();
    } finally {
      handling = false;
      console.log('done');
    }

    return false;
  }

  const mockPrice: Price = {
    amount: 0,
    currency: 'SEK',
    vat: 0,
    discount: 0,
  }

  const locale = getLocaleFromUrl(page.url);
  const percentDiscount = $derived(calculatePercentDiscount(product?.price ?? mockPrice));
</script>

<style>
  @keyframes shakeAnim {
    0% { transform: translateX(0); }
    20% { transform: translateX(-3px); }
    40% { transform: translateX(6px); }
    60% { transform: translateX(-6px); }
    80% { transform: translateX(3px); }
    100% { transform: translateX(0); }
  }

  .shake {
    position: relative;
    animation-name: shakeAnim;
    animation-duration: 0.39s;
    animation-iteration-count: once;
    animation-timing-function: ease-in;
  }
</style>

{#if loading}
<Card {...props} css={{ ...classes.root, ...cssProp, gap: '3' }} spacing="none">
  <Skeleton height={89} width={89} />
  <div
    class={css({
      display: 'flex',
      position: 'relative',
      flexDir: 'column',
      flex: 1,
      gap: '1',
    })}
  >
    <CardHeader css={{...classes.header, flex: '1!' }}>
      <span class={css(classes.brandName)}>
          <Skeleton width={80} />
      </span>
      <h3 class={css(classes.title)}>
          <Skeleton width={160} />
      </h3>
    </CardHeader>

    <CardFooter css={{ ...classes.footer, flex: '0!', gap: '3' }}>
      <div class={hstack({ gap: '2', width: '100%' })}>
        <span class={css(classes.price)}><Skeleton width={60}/></span>

      </div>

      <Skeleton height={33} width={99}/>
    </CardFooter>
  </div>
</Card>
{:else if product}
<a href={rewriteUrl(page, `/product/${product.handle}`)} onclick={onclick}>
  <Card {...props} css={{ ...classes.root, ...cssProp }} spacing="none">
    {#if loading}
      <Skeleton height={89} width={89} />
    {:else}
      <Image
        src={image?.url || '' /* TODO: default to an image that visuales that image is missing */ }
        background={`${image?.url}?width=8`}
        alt={m.productImageAlt({ "productName": product.name, "brandName": product.brand.name })}
        width={89}
        aspectRatio={1}
        css={{
          objectFit: 'contain!',
          maxHeight: 'items.2xl',
          alignSelf: 'center'
        }}
      />
    {/if}
    <div
      class={css({
        display: 'flex',
        position: 'relative',
        flexDir: 'column',
        flex: 1,
        gap: '1',
      })}
    >
      {#if percentDiscount > 0}
        <Badge size="sm" colorVariant="campaign" css={float.raw({mr: "6", mt: "4"})}>{percentDiscount}%</Badge>
      {/if}
      <CardHeader css={{...classes.header, flex: '1!' }}>
        <span class={css(classes.brandName)}>
          {#if loading}
            <Skeleton />
          {:else}
            {product.brand.name}
          {/if}
        </span>
        <h3 class={css(classes.title)}>
          {#if loading}
            <Skeleton />
          {:else}
            {product.name}
          {/if}
        </h3>
      </CardHeader>

      <CardFooter css={{ ...classes.footer, flex: '0!', gap: '3' }}>
        <div class={hstack({ gap: '2', width: '100%' })}>
          {#if loading}
            <span class={css(classes.price)}><Skeleton /></span>
          {:else if product.price.discount}
            <span class={css(classes.campaignPrice)}>{getLocaleFormattedPrice(product.price, locale).totalPrice}</span>
            <span class={css(classes.originalPrice)}>{getLocaleFormattedPrice(product.price, locale).originalPrice}</span>
          {:else}
            <span class={css(classes.price)}>{getLocaleFormattedPrice(product.price, locale).totalPrice}</span>
        {/if}
        </div>

        <span bind:this={buttonRef}>
          <Button
            disabled={outOfStock || handling || loading || added}
            variant={added ? 'secondary' : 'primary'}
            size="sm"
            fullWidth
            onclick={handleCartAdd}
          >
            {outOfStock ? m.outOfStock() : added ? m.addedShort() : buttonLabel}
            {#if handling || loading}
              <Loader size="xs" />
            {:else if added}
              <Check style="bold" />
            {:else if outOfStock}
              <X style="bold" />
            {:else}
              <ShoppingCartSimple style="bold" />
            {/if}
          </Button>
        </span>
      </CardFooter>
    </div>
  </Card>
</a>
{/if}
